import type { Utterance } from './utterance'
import type { VoiceActivity, VoiceDetector } from './voiceDetectors'

/**
 * Rules that decide when an utterance has ended. Durations are in ms.
 * There are two sets of rules (profiles): one for ordinary transcription and one for speaker channels.
 */
export interface SegmenterProfile {
  /** Minimum speech in an utterance. Shorter sounds (clicks, coughs) are not committed on their own. */
  minSpeechMs: number
  /** A pause of this length after speech ends the utterance. */
  endSilenceMs: number
  /** After an utterance gets this long, a shorter pause ends it (see `longUtteranceEndSilenceMs`). */
  longUtteranceMs: number
  /** The shorter pause that ends a long utterance, for example a pause between sentences in a monologue. */
  longUtteranceEndSilenceMs: number
  /** An utterance is committed after this time, even without any pause (e.g. continuous speech or noise). */
  maxUtteranceMs: number
  /**
   * When a pause follows too little speech (e.g. a click), forget that this speech started an utterance.
   * Its audio stays in the OpenAI buffer and becomes part of the next utterance.
   */
  dropShortUtterances: boolean
  /**
   * Commit audible sound that the detector never confirmed as speech after this time, for example a very quiet "yes".
   * Null: never commit unconfirmed sound on its own.
   */
  missedSpeechFlushMs: number | null
  /**
   * With two inputs: when the other input starts speaking at least this long after this input stopped,
   * end this input's utterance at once. Example: the user asks a question, the remote side answers.
   */
  handoffGapMs: number
  /**
   * At stop, commit the remaining audio if it is at least this long, also sound never confirmed as speech.
   * Null: commit only enough speech (`minSpeechMs`). Before the first commit, enough sound also counts:
   * the user may have spoken from the very first frame, so the detector took the speech for background noise.
   */
  minStopMs: number | null
}

/**
 * Rules for ordinary transcription: the browser microphone, or the mixed audio of the capture app.
 * Tuned by evaluating OpenAI transcription of noisy and telephone audio.
 */
export const ordinaryProfile: SegmenterProfile = {
  minSpeechMs: 300,
  endSilenceMs: 1500,
  longUtteranceMs: 30000,
  longUtteranceEndSilenceMs: 300,
  maxUtteranceMs: 60000,
  dropShortUtterances: true,
  missedSpeechFlushMs: null,
  handoffGapMs: 120,
  minStopMs: null,
}

/**
 * Rules for the speaker channels: the separate microphone and system audio of the capture app.
 * The detector of these channels already ignores most short noises, so a short minimum keeps quiet replies like "mhm".
 * The two-second pause keeps longer phrases together. When the other side starts talking, the turn ends sooner.
 */
export const speakerChannelProfile: SegmenterProfile = {
  minSpeechMs: 60,
  endSilenceMs: 2000,
  longUtteranceMs: 30000,
  longUtteranceEndSilenceMs: 500,
  maxUtteranceMs: 60000,
  dropShortUtterances: false,
  missedSpeechFlushMs: 15000,
  handoffGapMs: 120,
  minStopMs: 100,
}

/**
 * A boundary that the segmenter found in one input:
 * `start` when speech starts in the current utterance, `commit` when the utterance ends.
 */
export interface UtteranceBoundary {
  type: 'start' | 'commit'
  /** Index of the input: 0 for the first detector passed to the segmenter, 1 for the second. */
  input: number
  utterance: Utterance
}

/** The result of one {@link SpeechSegmenter.process} call. */
export interface SegmenterOutput {
  boundaries: UtteranceBoundary[]
  /** The inputs whose stop has completed (see {@link SpeechSegmenter.finish}). */
  ended: number[]
}

/** The current, not yet committed utterance of one input. */
class InputState {
  /** True while the input's OpenAI session accepts commits. */
  enabled = false
  /** Position of a requested stop. The final commit follows when the segmenter has received audio up to here. */
  stopFrame: number | null = null
  /** True after the first commit since the input was enabled. */
  committed = false
  utteranceId = 0
  firstFrame = 0
  firstSpeechFrame: number | null = null
  firstSoundFrame: number | null = null
  /** Amount of confirmed speech in the utterance, in frames. */
  speechFrames = 0
  /** Amount of audible sound in the utterance, in frames, also unconfirmed. */
  soundFrames = 0

  /** Start a new utterance at the given position. */
  reset(frame: number) {
    this.firstFrame = frame
    this.utteranceId++
    this.dropSpeech()
  }

  /** Forget the detected speech and sound, but keep the utterance start. */
  dropSpeech() {
    this.firstSpeechFrame = this.firstSoundFrame = null
    this.speechFrames = this.soundFrames = 0
  }

  utterance(endFrame: number): Utterance {
    return {
      id: this.utteranceId,
      firstFrame: this.firstFrame,
      firstSpeechFrame: this.firstSpeechFrame ?? this.firstSoundFrame ?? this.firstFrame,
      endFrame,
    }
  }
}

/**
 * Splits continuous audio into utterances and decides when to commit each one to OpenAI.
 *
 * Why: our OpenAI transcription sessions do not detect the end of speech themselves.
 * The widget must send a "commit" after each utterance. The commit tells OpenAI that the audio so far is complete,
 * and OpenAI then returns its final text as one transcript item. This class decides when to send the commits.
 *
 * How: a voice detector reports, for each short block of audio, whether it contains speech.
 * From these results, the segmenter reports two kinds of boundaries:
 * - `start`: speech started in the current utterance.
 * - `commit`: the utterance ended, mostly after a pause in speech. The caller sends the commit to OpenAI.
 * The segmenter does not change or cut audio. OpenAI receives all audio; the commits only decide where texts split.
 * This class only analyzes samples. It knows nothing about the browser or OpenAI.
 * An `UtteranceFeed` passes its boundaries on to one OpenAI session.
 *
 * Inputs: ordinary transcription has one input, the browser microphone or the mixed audio of the capture app.
 * Speaker attribution has two inputs, each with its own OpenAI session: the microphone (the local user, "Me")
 * and the system audio (the other call participants, "Remote").
 * With two inputs, one input's utterance also ends when the other input starts speaking after a short gap.
 * So a question and its answer become separate lines without waiting for the usual two-second pause.
 * Overlapping speech, like a short "mhm" while the other person talks, does not end the other person's utterance.
 *
 * Blocks: with `blockFrames`, the segmenter analyzes blocks of exactly this size (10 ms for the capture app),
 * independently of the size of the audio chunks that {@link process} receives.
 * Without `blockFrames`, each {@link process} call is one block (about 85 ms for the browser microphone).
 */
export class SpeechSegmenter {
  private readonly inputs: InputState[]
  /** Samples of an incomplete block, kept until the next {@link process} call completes it. */
  private readonly carry: Float32Array[]
  private carryLength = 0
  /** Number of frames received by {@link process}. */
  private fedFrame = 0
  /** Number of frames analyzed. Smaller than `fedFrame` while a block is incomplete. */
  private analyzedFrame = 0
  private readonly frames: (ms: number) => number

  /**
   * Create a segmenter with one detector for each input.
   * `sampleRate` converts the profile durations to frames.
   */
  constructor(
    private readonly detectors: VoiceDetector[],
    private readonly profile: SegmenterProfile,
    sampleRate: number,
    private readonly blockFrames?: number,
  ) {
    this.inputs = detectors.map(() => new InputState())
    this.carry = detectors.map(() => new Float32Array(blockFrames ?? 0))
    this.frames = ms => (ms * sampleRate) / 1000
  }

  /**
   * Start or stop segmenting one input, for example when its OpenAI session connects or disconnects.
   * Enabling starts a new utterance. The other input is not affected.
   * Speech detection continues while an input is disabled, so the detector keeps learning the background noise.
   */
  setEnabled(input: number, enabled: boolean) {
    const state = this.inputs[input]
    state.enabled = enabled
    state.stopFrame = null
    if (enabled) {
      state.committed = false
      state.reset(this.fedFrame)
    }
  }

  /**
   * Stop one input: commit its last utterance once the audio up to `stopFrame` has arrived.
   * `stopFrame` is the amount of audio received before the stop, including audio still waiting in a queue,
   * so the last words are not lost.
   * When {@link process} reaches `stopFrame`, it reports the final commit (see `minStopMs`),
   * lists the input in `ended`, and disables the input.
   * Return false when the input is disabled. No final commit follows then.
   */
  finish(input: number, stopFrame: number) {
    const state = this.inputs[input]
    if (state.enabled) state.stopFrame = stopFrame
    return state.enabled
  }

  /**
   * Analyze the next audio, one array of equal length for each input.
   * Return the boundaries found, and the inputs whose stop has completed.
   */
  process(samples: Float32Array[]): SegmenterOutput {
    const output: SegmenterOutput = { boundaries: [], ended: [] }
    const count = samples[0]?.length ?? 0
    if (!this.blockFrames) {
      if (count) this.analyze(samples, output.boundaries)
    } else {
      // Collect fixed-size blocks across calls. A 4096-sample chunk is not a whole number of 480-sample blocks.
      for (let offset = 0; offset < count; ) {
        const take = Math.min(this.blockFrames - this.carryLength, count - offset)
        this.carry.forEach((block, index) => block.set(samples[index].subarray(offset, offset + take), this.carryLength))
        this.carryLength += take
        offset += take
        if (this.carryLength === this.blockFrames) {
          this.analyze(this.carry, output.boundaries)
          this.carryLength = 0
        }
      }
    }
    this.fedFrame += count

    this.inputs.forEach((state, index) => {
      if (state.stopFrame === null || this.fedFrame < state.stopFrame) return
      if (this.shouldCommitAtStop(state)) output.boundaries.push(this.commit(index, this.fedFrame))
      output.ended.push(index)
      state.enabled = false
      state.stopFrame = null
    })
    return output
  }

  /** Run the detectors on one block of each input, and apply the rules of the profile. */
  private analyze(blocks: Float32Array[], boundaries: UtteranceBoundary[]) {
    const { profile } = this
    const length = blocks[0].length
    const firstFrame = this.analyzedFrame
    const endFrame = firstFrame + length
    this.analyzedFrame = endFrame
    // Let the detectors learn the background noise before the OpenAI session is ready.
    // After that, the user may speak, and speech must not be learned as background noise.
    const activity = this.detectors.map((detector, index) =>
      detector.process(blocks[index], firstFrame, !this.inputs[index].enabled),
    )

    this.inputs.forEach((state, index) => {
      const current = activity[index]
      const other: VoiceActivity | undefined = activity[1 - index]
      if (!state.enabled) return
      if (current.sound) {
        state.firstSoundFrame ??= firstFrame
        state.soundFrames += length
      }
      // Count speech only if it continues into the current utterance, not speech that ended before it started.
      if (current.speech && current.lastSpeechFrame! > state.firstFrame) {
        state.speechFrames += length
        if (state.firstSpeechFrame === null) {
          state.firstSpeechFrame = Math.max(state.firstFrame, current.onsetFrame ?? firstFrame)
          boundaries.push({ type: 'start', input: index, utterance: state.utterance(endFrame) })
        }
      }
      // A stopping input commits everything at once when the stop position is reached.
      if (state.stopFrame !== null) return

      // Measure the pause from the last loud block, also unconfirmed: it can be the start of the next word.
      const silence = endFrame - (current.lastLoudFrame ?? endFrame)
      const duration = endFrame - (state.firstSpeechFrame ?? state.firstSoundFrame ?? endFrame)
      if (state.firstSpeechFrame !== null) {
        // A long utterance ends at a shorter pause.
        const endSilence = this.frames(
          duration >= this.frames(profile.longUtteranceMs) ? profile.longUtteranceEndSilenceMs : profile.endSilenceMs,
        )
        // The other input started speaking after this input stopped: the turn has passed to the other side.
        const handoff =
          !!other?.speech &&
          other.onsetFrame !== null &&
          current.lastSpeechFrame !== null &&
          other.onsetFrame - current.lastSpeechFrame >= this.frames(profile.handoffGapMs)
        const ended =
          silence >= endSilence ||
          (handoff && silence >= this.frames(profile.handoffGapMs)) ||
          duration >= this.frames(profile.maxUtteranceMs)
        if (ended && this.hasEnoughSpeech(state)) {
          boundaries.push(this.commit(index, endFrame))
        } else if (silence >= endSilence && profile.dropShortUtterances) {
          // A short noise, then a pause: let the noise become part of the next utterance.
          state.dropSpeech()
        }
      } else if (
        state.firstSoundFrame !== null &&
        profile.missedSpeechFlushMs !== null &&
        duration >= this.frames(profile.missedSpeechFlushMs)
      ) {
        // Sound that the detector never confirmed as speech: commit it after a while, so its text still appears.
        boundaries.push(this.commit(index, endFrame))
      }
    })
  }

  /** Report whether the current utterance of an input has enough speech to commit. */
  private hasEnoughSpeech(state: InputState) {
    return state.speechFrames >= this.frames(this.profile.minSpeechMs)
  }

  /** Decide whether to commit the rest of an input at stop (see `minStopMs`). */
  private shouldCommitAtStop(state: InputState) {
    const { minStopMs, minSpeechMs } = this.profile
    if (minStopMs !== null) return this.fedFrame - state.firstFrame >= this.frames(minStopMs)
    return this.hasEnoughSpeech(state) || (!state.committed && state.soundFrames >= this.frames(minSpeechMs))
  }

  /** End the current utterance of one input at `endFrame`, and start the next utterance there. */
  private commit(input: number, endFrame: number): UtteranceBoundary {
    const state = this.inputs[input]
    const boundary: UtteranceBoundary = { type: 'commit', input, utterance: state.utterance(endFrame) }
    state.committed = true
    state.reset(endFrame)
    return boundary
  }
}
